import type { ApiError } from "@base/model/types/error";
import { isAxiosError } from "axios";
import { type ClassValue, clsx } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * tailwind.config의 커스텀 글자 크기. 알려 주지 않으면 tailwind-merge가 `text-label3`을 글자 색으로 보고
 * 뒤에 오는 `text-gray-800` 같은 색 클래스와 충돌한다고 판단해 지운다.
 */
const FONT_SIZE_TOKENS = [
  "h1",
  "h2",
  "h3",
  "title1",
  "title2",
  "title3",
  "title4",
  "title5",
  "title6",
  "subtitle1",
  "subtitle2",
  "subtitle3",
  "subtitle4",
  "subtitle5",
  "subtitle6",
  "body1",
  "body2",
  "body3",
  "body4",
  "caption1",
  "label1",
  "label2",
  "label3",
];

const twMerge = extendTailwindMerge({
  extend: { classGroups: { "font-size": [{ text: FONT_SIZE_TOKENS }] } },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const isBrowser = typeof window !== "undefined";

export const getCookie = (name: string) => {
  if (!isBrowser) {
    return null;
  }

  if (!document.cookie || document.cookie === "") {
    return null;
  }

  const cookies = document.cookie.split(";");

  for (const cookie of cookies) {
    const trimmedCookie = cookie.trim();
    if (trimmedCookie.substring(0, name.length + 1) !== `${name}=`) {
      continue;
    }

    return decodeURIComponent(trimmedCookie.substring(name.length + 1));
  }

  return null;
};

export const isApiError = (error: unknown): error is ApiError => {
  return (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    typeof (error as ApiError).status === "number" &&
    "message" in error &&
    typeof (error as ApiError).message === "string" &&
    "code" in error &&
    typeof (error as ApiError).code === "string"
  );
};

export const isAxiosApiError = (error: unknown): error is { response: { data: ApiError } } => {
  return isAxiosError(error) && error.response?.data && isApiError(error.response.data);
};
